import { useMemo } from 'react'
import type { FC } from 'react'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import type { Block, Document as RichTextDocument } from '@contentful/rich-text-types'
import Link from 'next/link'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import Typography from '@segment/matcha/components/Typography'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronLeftIcon } from '@segment/matcha/components/Icons/Common'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import NavigationV2 from '@segment/templates/Navigation2'
import Footer from '@segment/templates/Footer/Footer'
import GettingStarted from '@segment/templates/GettingStarted'

import { useSingleResourceQuery } from '@segment/api/routes/resources/queries'

import useRichTextTableOfContent, { getNodeText } from '@segment/utils/useRichTextTableOfContent'

import Layout from '@segment/next-shared/layout'

import AdditionalResources from '../components/AdditionalResources'
import ResourcesDivider from '../components/ResourcesDivider'

import ResourcePromo from './components/ResourcePromo'

import Content from './components/Content'
import Sidebar from './components/Sidebar'
import Faq from './components/Faq'

interface ResourceSinglePageViewProps {
  categorySlug?: string
  slug: string
}

const resourcesButton = {
  text: `Resources`,
  href: `/resources`,
}

const ResourcesSinglePageView: FC<ResourceSinglePageViewProps> = ({ categorySlug, slug }) => {
  const theme = useTheme()

  const { data } = useSingleResourceQuery({ args: [categorySlug ? `${categorySlug}/${slug}` : `${slug}`] })
  if (!data) return null

  const { faqs, title, content, socialTitle, socialImage, socialDescription, resourcePromo } = data

  const tableOfContentState = useRichTextTableOfContent(content as RichTextDocument)
  const contentHeadingsOptions = useMemo(
    () =>
      tableOfContentState.getMenuLevelItems(0).map(([node, id]) => ({ text: getNodeText(node as Block), value: id })),
    [tableOfContentState]
  )

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle={socialTitle}
        pageDescription={socialDescription}
        pageOG={socialImage.src}
        bodyBackground={theme.palette.neutrals.white[100]}
        themeColor={theme.palette.neutrals.white[100]}
      >
        <NavigationV2 />
        <Main>
          <SectionBase variant="sidebar" sidebarSize="small" sticky={true}>
            <Section.Sidebar as="aside">
              <Section.Sticky>
                <Link href={resourcesButton.href}>
                  <StyledButtonText tag="span" iconStart={ChevronLeftIcon}>
                    {resourcesButton.text}
                  </StyledButtonText>
                </Link>
                <Sidebar id="sidebar" data={contentHeadingsOptions} />
              </Section.Sticky>
            </Section.Sidebar>
            <Section.Contents>
              <Heading variant="h1">{title}</Heading>
              <Content data={content as RichTextDocument} tableOfContentState={tableOfContentState} />
              {faqs && <Faq data={faqs} />}
              {resourcePromo && <ResourcePromo {...resourcePromo} />}
            </Section.Contents>
          </SectionBase>
          <ResourcesDivider />
          <AdditionalResources />
          <GettingStarted />
        </Main>
        <Footer />
      </Layout>
    </AppearanceProvider>
  )
}

export default ResourcesSinglePageView

const Main = styled.main`
  background-color: ${({ theme }) => theme.palette.neutrals.white[100]};
`

const StyledButtonText = styled(ButtonText)`
  margin-bottom: 30px;
  ${({ theme }) => theme.breakpoints.up(`md`)} {
    margin-bottom: 38px;
  }
  ${({ theme }) => theme.breakpoints.up(`xxl`)} {
    margin-bottom: 46px;
  }
`

const Heading = styled(Typography)`
  margin-top: 0;
`
